import React from "react";
import { motion } from "framer-motion";
import { Check, MessageCircle } from "lucide-react";
import {
  Container,
  Overline,
  AxorButton,
  EASE,
} from "@/components/primitives";
import { PageHero } from "@/components/PageHero";
import {
  PrismBackground,
  PrismHero,
  PrismNodes,
  PrismOutline,
  FloatingShard,
} from "@/components/Prism";
import { CTASection } from "@/components/CTASection";
import { services } from "@/data/services";
import { waLink } from "@/config";

// Distinct prism visual per service, alternating sides.
const ServiceVisual = ({ index }) => {
  const variants = [
    <PrismHero className="w-36" key="a" />,
    <PrismNodes className="w-full max-w-sm" key="b" />,
    <div key="c" className="relative">
      <div className="absolute inset-0 rounded-full bg-axor-cyan/10 blur-3xl" />
      <PrismOutline className="relative animate-spin-slow" size={150} />
    </div>,
    <PrismHero className="w-36" key="d" />,
    <PrismNodes className="w-full max-w-sm" key="e" />,
    <div key="f" className="relative">
      <div className="absolute inset-0 rounded-full bg-axor-turquoise/10 blur-3xl" />
      <PrismOutline className="relative" size={150} />
    </div>,
  ];
  return (
    <div className="relative flex items-center justify-center">
      <FloatingShard className="right-6 top-4 h-7 w-7" delay={0.4} />
      {variants[index % variants.length]}
    </div>
  );
};

export default function Services() {
  return (
    <>
      <PageHero
        overline="Our Services"
        title="Career Support That Goes Beyond The Resume."
        highlight="Beyond The Resume"
        description="Practical, personalized support at every stage — from first steps to your next big move."
        testid="services-hero"
      />

      <div className="relative overflow-hidden">
        <PrismBackground />
        {services.map((s, i) => {
          const reversed = i % 2 === 1;
          return (
            <section
              key={s.id}
              className={`relative border-t border-white/[0.05] py-20 md:py-24 ${i % 2 === 1 ? "bg-axor-deep" : ""}`}
              data-testid={`service-${s.id}`}
            >
              <Container>
                <div className={`grid items-center gap-12 lg:grid-cols-2 ${reversed ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: EASE }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-axor-cyan/25 bg-axor-cyan/10 text-axor-cyan">
                        <s.icon className="h-7 w-7" />
                      </span>
                      <span className="text-5xl font-bold text-white/[0.08]">{s.number}</span>
                    </div>
                    <h2 className="mt-6 text-3xl font-semibold tracking-tight text-axor-white sm:text-4xl">
                      {s.title}
                    </h2>
                    <p className="mt-4 max-w-lg text-lg leading-relaxed text-axor-text">
                      {s.description}
                    </p>
                    <ul className="mt-7 space-y-3">
                      {s.benefits.map((b) => (
                        <li key={b} className="flex items-center gap-3 text-sm font-medium text-axor-white/85">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-axor-cyan/15 text-axor-cyan">
                            <Check className="h-3.5 w-3.5" />
                          </span>
                          {b}
                        </li>
                      ))}
                    </ul>
                    <AxorButton
                      href={waLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="secondary"
                      icon={MessageCircle}
                      className="mt-8"
                      data-testid={`service-cta-${s.id}`}
                    >
                      Talk To Axor
                    </AxorButton>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                    className="relative h-72"
                  >
                    <ServiceVisual index={i} />
                  </motion.div>
                </div>
              </Container>
            </section>
          );
        })}
      </div>

      <CTASection
        overline="Get Started"
        title="Let's Build Your Next Move."
        description="Your next opportunity starts with the right direction."
      />
    </>
  );
}
